import { useState } from "react";
import { toast } from "sonner";
import { Mail, Handshake, Users } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";

const SUBJECTS = ["General Enquiries", "Manufacturer / Brand Partnerships", "Community"];

const CARDS = [
  { icon: Mail, title: "General Enquiries", desc: "Questions about the project, the fish, or the journey? Drop a message — or email us directly.", email: "info@thefishhouseproject.co.uk" },
  { icon: Handshake, title: "Manufacturer / Brand Partnerships", desc: "Interested in working with The Fish House Project? We're interested in aquarium equipment, fishkeeping products and future Fish House Test Lab partnerships.", email: "info@thefishhouseproject.co.uk" },
  { icon: Users, title: "Community", desc: "Want to get involved, suggest a project or become a future community tester? Say hello." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-brand-elevated border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

  return (
    <>
      <PageHeader label="Contact" title="GET IN TOUCH">
        Whether you're a viewer, a fellow fishkeeper or a brand — here's how to reach the project.
      </PageHeader>

      <section className="pb-28" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="space-y-5">
              {CARDS.map(({ icon: Icon, title, desc, email }) => (
                <div key={title} className="card-surface p-6 flex gap-4" data-testid={`contact-card-${title.split(" ")[0].toLowerCase()}`}>
                  <span className="h-11 w-11 shrink-0 grid place-items-center rounded-full bg-brand-cyan/10 text-brand-cyan">
                    <Icon size={20} />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-white">{title}</h3>
                    <p className="mt-1.5 text-brand-textSec text-sm leading-relaxed">{desc}</p>
                    {email && (
                      <a href={`mailto:${email}`} className="mt-2 inline-block text-brand-cyan text-sm font-medium hover:underline" data-testid={`contact-email-${title.split(" ")[0].toLowerCase()}`}>
                        {email}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="card-surface p-7 sm:p-8" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-brand-textSec mb-2 block">Name</label>
                  <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-name-input" placeholder="Your name" />
                </div>
                <div>
                  <label className="text-sm text-brand-textSec mb-2 block">Email</label>
                  <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-email-input" placeholder="you@email.com" />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm text-brand-textSec mb-2 block">Subject</label>
                <select value={form.subject} onChange={update("subject")} className={inputCls} data-testid="contact-subject-select">
                  {SUBJECTS.map((s) => <option key={s} value={s} className="bg-brand-elevated">{s}</option>)}
                </select>
              </div>
              <div className="mt-4">
                <label className="text-sm text-brand-textSec mb-2 block">Message</label>
                <textarea required rows={5} value={form.message} onChange={update("message")} className={inputCls} data-testid="contact-message-input" placeholder="Tell me more…" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary mt-6 w-full py-3.5 text-sm disabled:opacity-60" data-testid="contact-submit-btn">
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
